/**
 * Rules Engine
 * Validates token requests against business rules
 */
import logger from './logger';
import dayjs from 'dayjs';
import {
  getConsecutiveDays,
  dateRangesOverlap,
  getDatesInRange,
} from './dateHelpers';
import requestService from '../services/requestService';
import employeeService from '../services/employeeService';
import settingsService from '../services/settingsService';
import centerService from '../services/centerService';

/**
 * Rule 1: Max Consecutive Days Rule
 * Cannot exceed 3 working days per request
 */
export const maxConsecutiveDaysRule = async (request) => {
  try {
    logger.debug('Evaluating maxConsecutiveDaysRule', { request });
    
    // Use the 'days' field from request which is already calculated as working days
    const days = request.days || getConsecutiveDays(request.startDate, request.endDate);
    const maxDays = 3;
    
    if (days > maxDays) {
      const message = `Request exceeds maximum consecutive working days. Requested: ${days}, Maximum: ${maxDays}`;
      logger.warning('maxConsecutiveDaysRule failed', { request, days, maxDays });
      return { passed: false, message };
    }
    
    logger.debug('maxConsecutiveDaysRule passed', { request, days });
    return { passed: true, message: 'Working days within limit' };
  } catch (error) {
    logger.error('Error in maxConsecutiveDaysRule', error, { request });
    return { passed: false, message: 'Error validating consecutive days rule' };
  }
};

/**
 * Rule 2: Yearly Token Limit Rule
 * Cannot exceed annual token quota
 */
export const yearlyTokenLimitRule = async (request) => {
  try {
    logger.debug('Evaluating yearlyTokenLimitRule', { request });
    
    const employee = await employeeService.getEmployeeById(request.employeeId);
    if (!employee) {
      return { passed: false, message: 'Employee not found' };
    }
    
    const settings = await settingsService.getSettings();
    const yearlyLimit = employee.yearlyTokens || settings.yearlyTokenLimit || 12;
    const tokensUsed = employee.tokensUsed || 0;
    
    // Also check pending requests to prevent over-booking
    const allRequests = await requestService.getAllRequests();
    const pendingRequests = allRequests.filter(
      r => r.employeeId === request.employeeId && 
           r.status === 'pending' &&
           r.id !== request.id // Exclude current request if editing
    );
    const pendingTokens = pendingRequests.reduce((sum, r) => sum + (r.days || 0), 0);
    
    // Use the 'days' field from request which is already calculated as working days
    const requestedDays = request.days || getConsecutiveDays(request.startDate, request.endDate);
    const totalTokens = tokensUsed + pendingTokens + requestedDays;
    
    if (totalTokens > yearlyLimit) {
      const message = `Request would exceed yearly token limit. Approved: ${tokensUsed}, Pending: ${pendingTokens}, Requested: ${requestedDays}, Total: ${totalTokens}, Limit: ${yearlyLimit}`;
      logger.warning('yearlyTokenLimitRule failed', {
        request,
        tokensUsed,
        pendingTokens,
        requestedDays,
        totalTokens,
        yearlyLimit,
      });
      return { passed: false, message };
    }
    
    logger.debug('yearlyTokenLimitRule passed', {
      request,
      tokensUsed,
      pendingTokens,
      requestedDays,
      totalTokens,
      yearlyLimit,
    });
    return { passed: true, message: 'Within yearly token limit' };
  } catch (error) {
    logger.error('Error in yearlyTokenLimitRule', error, { request });
    return { passed: false, message: 'Error validating yearly token limit rule' };
  }
};

/**
 * Rule 3: Team Distribution Rule
 * Warning if too many team members apply on same date
 */
export const teamDistributionRule = async (request) => {
  try {
    logger.debug('Evaluating teamDistributionRule', { request });
    
    const employee = await employeeService.getEmployeeById(request.employeeId);
    if (!employee || !employee.managerId) {
      // No manager means no team, rule passes
      return { passed: true, message: 'No team distribution check needed' };
    }
    
    const teamMembers = await employeeService.getEmployeesByManager(employee.managerId);
    const requestDates = getDatesInRange(request.startDate, request.endDate);
    
    // Get all approved/pending requests for team members
    const allRequests = await requestService.getAllRequests();
    const teamRequests = allRequests.filter(
      req =>
        teamMembers.some(member => member.id === req.employeeId) &&
        ['pending', 'approved'].includes(req.status) &&
        req.id !== request.id // Exclude current request
    );
    
    // Count overlapping requests per date
    const dateCounts = {};
    requestDates.forEach(date => {
      dateCounts[date] = teamRequests.filter(req => {
        const reqDates = getDatesInRange(req.startDate, req.endDate);
        return reqDates.includes(date);
      }).length;
    });
    
    // Check if any date has more than 50% of team members
    const maxOverlap = Math.max(...Object.values(dateCounts), 0);
    const teamSize = teamMembers.length;
    const threshold = Math.ceil(teamSize * 0.5); // 50% threshold
    
    if (maxOverlap >= threshold && teamSize > 1) {
      const message = `Warning: ${maxOverlap} team member(s) already have requests on overlapping dates. This may impact team coverage.`;
      logger.warning('teamDistributionRule warning', {
        request,
        maxOverlap,
        teamSize,
        threshold,
      });
      return { passed: true, message, warning: true };
    }
    
    logger.debug('teamDistributionRule passed', { request, maxOverlap, teamSize });
    return { passed: true, message: 'Team distribution acceptable' };
  } catch (error) {
    logger.error('Error in teamDistributionRule', error, { request });
    return { passed: true, message: 'Error checking team distribution (rule passed with warning)' };
  }
};

/**
 * Rule 4: Blackout Days Rule
 * Cannot request on blackout days (center-specific or global)
 */
export const blackoutDaysRule = async (request) => {
  try {
    logger.debug('Evaluating blackoutDaysRule', { request });
    
    // Get employee to find their center
    const employee = await employeeService.getEmployeeById(request.employeeId);
    if (!employee) {
      return { passed: false, message: 'Employee not found' };
    }
    
    let blackoutDays = [];
    
    // First, check center-specific blackout days
    if (employee.centerId) {
      try {
        const center = await centerService.getCenterById(employee.centerId);
        if (center && center.blackoutDays && center.blackoutDays.length > 0) {
          blackoutDays = center.blackoutDays;
          logger.debug('Using center-specific blackout days', { centerId: employee.centerId, blackoutDays });
        }
      } catch (error) {
        logger.warning('Error fetching center blackout days, falling back to global', { error, centerId: employee.centerId });
      }
    }
    
    // Note: No longer falling back to global blackout days from settings
    // Blackout days are now only managed per center
    if (blackoutDays.length === 0) {
      return { passed: true, message: 'No blackout days configured' };
    }
    
    const requestDates = getDatesInRange(request.startDate, request.endDate);
    const conflictingDates = requestDates.filter(date => blackoutDays.includes(date));
    
    if (conflictingDates.length > 0) {
      const centerName = employee.centerId ? (await centerService.getCenterById(employee.centerId))?.name : 'global';
      const message = `Request conflicts with ${centerName} blackout days: ${conflictingDates.join(', ')}`;
      logger.warning('blackoutDaysRule failed', { request, conflictingDates, centerId: employee.centerId });
      return { passed: false, message };
    }
    
    logger.debug('blackoutDaysRule passed', { request, centerId: employee.centerId });
    return { passed: true, message: 'No blackout day conflicts' };
  } catch (error) {
    logger.error('Error in blackoutDaysRule', error, { request });
    return { passed: false, message: 'Error validating blackout days rule' };
  }
};

/**
 * Rule 5: Overlap Request Rule
 * Cannot have overlapping requests
 */
export const overlapRequestRule = async (request) => {
  try {
    logger.debug('Evaluating overlapRequestRule', { request });
    
    const employeeRequests = await requestService.getRequestsByEmployee(request.employeeId);
    
    // Filter to only pending or approved requests (exclude rejected and current request)
    const activeRequests = employeeRequests.filter(
      req => ['pending', 'approved'].includes(req.status) && req.id !== request.id
    );
    
    for (const existingRequest of activeRequests) {
      if (
        dateRangesOverlap(
          request.startDate,
          request.endDate,
          existingRequest.startDate,
          existingRequest.endDate
        )
      ) {
        const message = `Request overlaps with existing ${existingRequest.status} request from ${existingRequest.startDate} to ${existingRequest.endDate}`;
        logger.warning('overlapRequestRule failed', {
          request,
          overlappingRequest: existingRequest,
        });
        return { passed: false, message };
      }
    }
    
    logger.debug('overlapRequestRule passed', { request });
    return { passed: true, message: 'No overlapping requests' };
  } catch (error) {
    logger.error('Error in overlapRequestRule', error, { request });
    return { passed: false, message: 'Error validating overlap rule' };
  }
};

/**
 * Rule 6: Advance Notice Rule
 * Request must be submitted at least the configured minimum days in advance
 */
export const advanceNoticeRule = async (request) => {
  try {
    logger.debug('Evaluating advanceNoticeRule', { request });
    
    const settings = await settingsService.getSettings();
    const minAdvanceDays = settings.minAdvanceNoticeDays || 14; // Default to 14 days if not configured
    
    // If minAdvanceNoticeDays is 0, skip this rule (no advance notice required)
    if (minAdvanceDays === 0) {
      logger.debug('advanceNoticeRule skipped (minAdvanceNoticeDays is 0)', { request });
      return { passed: true, message: 'No advance notice requirement configured' };
    }
    
    const today = dayjs().startOf('day');
    const startDate = dayjs(request.startDate).startOf('day');
    const daysUntilStart = startDate.diff(today, 'day');
    
    if (daysUntilStart < minAdvanceDays) {
      const weeks = Math.round(minAdvanceDays / 7 * 10) / 10; // Calculate weeks for display
      const message = `Request must be submitted at least ${minAdvanceDays} day${minAdvanceDays !== 1 ? 's' : ''} (${weeks} week${weeks !== 1 ? 's' : ''}) in advance. Your start date is only ${daysUntilStart} day(s) away.`;
      logger.warning('advanceNoticeRule failed', {
        request,
        daysUntilStart,
        minAdvanceDays,
      });
      return { passed: false, message };
    }
    
    logger.debug('advanceNoticeRule passed', { request, daysUntilStart, minAdvanceDays });
    return { passed: true, message: `Request submitted with ${daysUntilStart} days advance notice` };
  } catch (error) {
    logger.error('Error in advanceNoticeRule', error, { request });
    return { passed: false, message: 'Error validating advance notice rule' };
  }
};

/**
 * Evaluate all rules for a request
 */
export const evaluateAllRules = async (request) => {
  try {
    logger.info('Evaluating all rules for request', { request });
    
    const rules = [
      { name: 'maxConsecutiveDays', rule: maxConsecutiveDaysRule },
      { name: 'advanceNotice', rule: advanceNoticeRule },
      { name: 'yearlyTokenLimit', rule: yearlyTokenLimitRule },
      { name: 'blackoutDays', rule: blackoutDaysRule },
      { name: 'overlapRequest', rule: overlapRequestRule },
      { name: 'teamDistribution', rule: teamDistributionRule }, // Warning only, evaluated last
    ];
    
    const results = [];
    let allPassed = true;
    let hasWarning = false;
    
    for (const { name, rule } of rules) {
      const result = await rule(request);
      results.push({
        ruleName: name,
        ...result,
      });
      
      if (!result.passed) {
        allPassed = false;
      }
      
      if (result.warning) {
        hasWarning = true;
      }
    }
    
    const summary = {
      passed: allPassed,
      hasWarning,
      results,
      message: allPassed
        ? hasWarning
          ? 'All rules passed with warnings'
          : 'All rules passed'
        : 'Some rules failed',
    };
    
    logger.info('Rule evaluation complete', {
      request,
      summary,
    });
    
    return summary;
  } catch (error) {
    logger.error('Error evaluating rules', error, { request });
    return {
      passed: false,
      hasWarning: false,
      results: [],
      message: 'Error evaluating rules',
    };
  }
};

